import React, { useState, useEffect } from 'react';
import api from '../config/api';
import OvertimeModal from '../components/OvertimeModal';
import './ManagementPages.css';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

const Overtime = () => {
  const [entries, setEntries] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editEntry, setEditEntry] = useState(null);

  const [filterEmployee, setFilterEmployee] = useState('');
  const [filterMonth, setFilterMonth] = useState(new Date().toLocaleString('default', { month: 'long' }));
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());

  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    fetchEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterEmployee, filterMonth, filterYear]);

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterEmployee) params.employee = filterEmployee;
      if (filterMonth) params.month = filterMonth;
      if (filterYear) params.year = filterYear;
      const res = await api.get('/api/overtime', { params });
      setEntries(res.data);
    } catch (err) {
      setError('Failed to load overtime records');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await api.get('/api/employees');
      setEmployees(res.data);
    } catch (err) {
      console.error('Failed to load employees');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this overtime record?')) return;
    try {
      await api.delete(`/api/overtime/${id}`);
      fetchEntries();
    } catch (err) {
      setError('Failed to delete overtime record');
    }
  };

  const openAdd = () => {
    setEditEntry(null);
    setShowModal(true);
  };

  const openEdit = (entry) => {
    setEditEntry(entry);
    setShowModal(true);
  };

  const handleSaved = () => {
    fetchEntries();
  };

  const totalAmount = entries.reduce((sum, e) => sum + e.overtimeAmount, 0);

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="management-page">
      <div className="page-header">
        <h1>Overtime</h1>
        <button className="btn btn-primary" onClick={openAdd}>
          + Log Overtime
        </button>
      </div>

      {error && <div className="error">{error}</div>}

      <div className="filters">
        <select value={filterEmployee} onChange={e => setFilterEmployee(e.target.value)}>
          <option value="">All Employees</option>
          {employees.map(emp => (
            <option key={emp._id} value={emp._id}>{emp.name}</option>
          ))}
        </select>
        <select value={filterMonth} onChange={e => setFilterMonth(e.target.value)}>
          <option value="">All Months</option>
          {MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        <input
          type="number"
          value={filterYear}
          onChange={e => setFilterYear(parseInt(e.target.value))}
          min="2020"
          max="2099"
          style={{ width: '90px' }}
        />
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
          <span style={{ fontSize: '13px', color: '#aaa' }}>
            Total: <span style={{ fontWeight: 'bold', color: '#4caf50', fontSize: '15px' }}>
              PKR {totalAmount.toFixed(2)}
            </span>
            <span style={{ color: '#666', marginLeft: '8px' }}>({entries.length} records)</span>
          </span>
        </div>
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Employee</th>
              <th>Date</th>
              <th>Type</th>
              <th>Hours</th>
              <th>Multiplier</th>
              <th>Daily Rate</th>
              <th>Overtime Amount</th>
              <th>Reason</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 ? (
              <tr>
                <td colSpan="9" style={{ textAlign: 'center', color: '#666', padding: '30px' }}>
                  No overtime records found
                </td>
              </tr>
            ) : entries.map(entry => {
              const dailyRate = entry.employee?.salary ? entry.employee.salary / 30 : 0;
              return (
                <tr key={entry._id}>
                  <td>
                    <div>{entry.employee?.name}</div>
                    <div style={{ fontSize: '11px', color: '#888' }}>{entry.employee?.position}</div>
                  </td>
                  <td>{new Date(entry.date).toLocaleDateString()}</td>
                  <td>
                    <span style={{
                      background: entry.type === 'full_day' ? '#0d3a50' : '#1a2a3a',
                      color: entry.type === 'full_day' ? '#4fc3f7' : '#90caf9',
                      padding: '3px 8px', borderRadius: '4px', fontSize: '12px'
                    }}>
                      {entry.type === 'full_day' ? 'Full Day' : `${entry.hours}h`}
                    </span>
                  </td>
                  <td style={{ color: '#aaa' }}>{entry.type === 'hours' ? entry.hours : '8'}</td>
                  <td>
                    <span style={{ color: '#f39c12', fontWeight: 'bold' }}>{entry.multiplier}x</span>
                  </td>
                  <td style={{ color: '#aaa' }}>PKR {dailyRate.toFixed(2)}</td>
                  <td style={{ fontWeight: 'bold', color: '#4caf50' }}>
                    PKR {entry.overtimeAmount?.toFixed(2)}
                  </td>
                  <td style={{ color: '#aaa', fontSize: '13px' }}>{entry.reason || '—'}</td>
                  <td>
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => openEdit(entry)}
                      title="Edit"
                    >
                      ✏️
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDelete(entry._id)}
                      title="Delete"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <OvertimeModal
        show={showModal}
        onClose={() => setShowModal(false)}
        employees={employees}
        defaultEmployeeId={filterEmployee}
        editEntry={editEntry}
        onSaved={handleSaved}
      />
    </div>
  );
};

export default Overtime;
